import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL;
const DEFAULT_LIMIT = 20;

function normalizeNumber(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }
  return parsed;
}

function normalizeArchivedPayload(payload, fallbackLimit = DEFAULT_LIMIT) {
  if (Array.isArray(payload)) {
    const count = payload.length;
    return {
      items: payload,
      total: count,
      page: 1,
      limit: count === 0 ? fallbackLimit : count
    };
  }

  const items = Array.isArray(payload?.items) ? payload.items : [];
  const total = normalizeNumber(payload?.total, items.length);
  const page = normalizeNumber(payload?.page, 1);
  const limit = normalizeNumber(payload?.limit, fallbackLimit);

  return { items, total, page, limit };
}

async function parseJsonSafely(res) {
  try {
    return await res.json();
  } catch (err) {
    return null;
  }
}

export function useArchivedRmas({
  deviceId,
  serialNumber,
  page = 1,
  limit = DEFAULT_LIMIT,
  enabled = true,
  path = 'rma-archive'
} = {}) {
  const { getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(page);
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestKey = useMemo(() => {
    const normalizedDeviceId = deviceId ? String(deviceId) : '';
    const normalizedSerial = serialNumber ? String(serialNumber) : '';
    return JSON.stringify({ normalizedDeviceId, normalizedSerial, page, limit, enabled, path });
  }, [deviceId, serialNumber, page, limit, enabled, path]);

  const fetchArchived = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      setTotal(0);
      setCurrentPage(page);
      setCurrentLimit(limit);
      setError(null);
      return;
    }

    const query = new URLSearchParams();
    query.set('status', 'archived');
    query.set('page', String(page));
    query.set('limit', String(limit));
    if (deviceId) {
      query.set('deviceId', String(deviceId));
    }
    if (serialNumber) {
      query.set('serialNumber', String(serialNumber));
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken?.({ template: 'service' }).catch(() => getToken?.());
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE}/${path}?${query.toString()}`, { headers });

      if (!res.ok) {
        const payload = await parseJsonSafely(res);
        const message = payload?.message || `Failed to fetch archived RMAs (status ${res.status})`;
        throw new Error(message);
      }

      const payload = await parseJsonSafely(res);
      const normalized = normalizeArchivedPayload(payload, limit);
      setItems(normalized.items);
      setTotal(normalized.total);
      setCurrentPage(normalized.page);
      setCurrentLimit(normalized.limit);
    } catch (err) {
      setError(err);
      setItems([]);
      setTotal(0);
      setCurrentPage(page);
      setCurrentLimit(limit);
    } finally {
      setIsLoading(false);
    }
  }, [deviceId, serialNumber, page, limit, enabled, getToken, path]);

  const refetchRef = useRef(fetchArchived);
  refetchRef.current = fetchArchived;

  useEffect(() => {
    refetchRef.current();
  }, [requestKey]);

  return {
    items,
    total,
    page: currentPage,
    limit: currentLimit,
    isLoading,
    error,
    refetch: useCallback(() => refetchRef.current(), [])
  };
}

export function useArchivedRmasByDevice({ deviceId, serialNumber, page, limit, enabled } = {}) {
  return useArchivedRmas({ deviceId, serialNumber, page, limit, enabled });
}

export default useArchivedRmas;
